<template>
  <f7-page name="create-group">
    <f7-navbar :title="$t('group.createGroup')" back-link no-shadow>
      <f7-nav-right>
        <span class="step-label"
          >{{ $t("common.creationStep") }} {{ step }}/{{ totalSteps }}</span
        >
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <div v-if="draftSaved" class="draft-saved">
          <f7-icon f7="checkmark_circle_fill" size="12" />{{
            $t("group.draftSaved")
          }}
        </div>

        <!-- avocash Delight Express Header Banner -->
        <f7-card class="express-banner" :padding="false">
          <div class="express-banner-inner">
            <div class="express-banner-icon">
              <f7-icon material="rocket_launch" size="22" />
            </div>
            <div class="express-banner-text">
              <div class="express-banner-meta">
                <f7-badge color="primary" class="express-banner-badge">
                  {{ $t("group.expressStep") }}
                </f7-badge>
                <span class="express-banner-time">
                  <f7-icon material="timer" size="13" />
                  {{ $t("group.expressTime") }}
                </span>
              </div>
              <h2 class="express-banner-title">{{
                $t("group.expressTitle")
              }}</h2>
              <p class="express-banner-desc">{{
                $t("group.expressDesc")
              }}</p>
            </div>
          </div>
        </f7-card>

        <GroupCreationSummary
          class="create-summary-mobile"
          variant="accordion"
          :category-code="form.category"
          :types="derivedTypes"
          :step="step"
        />

        <!-- STEP 1 · PURPOSE -->
        <f7-block-title large v-if="step === 1">{{
          $t("group.stepPurpose")
        }}</f7-block-title>
        <f7-block v-if="step === 1">
          <GroupCategoryPicker
            :model-value="form.category"
            @update:model-value="onCategoryChange"
          />

          <template v-if="form.category">
            <div class="selected-category-recap">
              {{ $t(groupCategoryLabel(form.category)) }} —
              {{ $t("groupSummary.descriptions." + form.category) }}
            </div>

            <f7-block-title>{{
              $t("group.creationPrimaryType")
            }}</f7-block-title>
            <GroupPrimaryTypePicker
              v-model="form.primaryType"
              :category="form.category"
            />

            <f7-block-title>
              {{ $t("group.creationSecondaryTypes") }}
            </f7-block-title>
            <GroupSecondaryTypePicker
              v-model="form.secondaryTypes"
              :category="form.category"
              :exclude="form.primaryType"
            />
          </template>
        </f7-block>

        <!-- STEP 2 · IDENTITY -->
        <f7-block-title large v-if="step === 2">{{
          $t("group.stepIdentity")
        }}</f7-block-title>
        <f7-list v-if="step === 2" inset strong outline dividers>
          <f7-list-input
            :label="$t('group.groupName')"
            type="text"
            :placeholder="$t('group.namePlaceholder')"
            :value="form.name"
            @input="form.name = $event.target.value"
          />
          <f7-list-input
            :label="$t('group.descriptionOptional')"
            :placeholder="$t('group.descriptionHint')"
            type="texteditor"
            resizable
            maxlength="280"
            mode="keyboard-toolbar"
            style="--f7-text-editor-height: 150px"
            :value="form.description"
            @texteditor:change="(value) => (form.description = value)"
          />
          <f7-list-item
            :header="$t('common.selectCountry')"
            class="selectCountry"
            smart-select
            :smart-select-params="{
              openIn: 'popup',
              searchbar: true,
              searchbarPlaceholder: $t('common.findCountry'),
              closeOnSelect: true,
              valueEl: '.country-value',
              sheetSwipeToClose: true,
            }"
          >
            <select name="country" v-model="selectedCountry">
              <optgroup
                v-for="(countryList, continentName, index) in countries"
                :label="continentName"
                :key="index"
              >
                <option
                  v-for="country in countryList"
                  :key="country.id"
                  :value="country.countryCode2"
                  :data-option-image="
                    'https://flagcdn.com/' +
                    country.countryCode2.toLowerCase() +
                    '.svg'
                  "
                  width="24"
                >
                  {{ country.name }} {{ country.countryCode2 }}
                  {{ country.dialCode }}
                </option>
              </optgroup>
            </select>
            <template #media>
              <img
                v-if="groupCountry"
                :src="
                  'https://flagcdn.com/' +
                  groupCountry.countryCode2.toLowerCase() +
                  '.svg'
                "
                width="24"
                class="country-flag"
              />
            </template>
            <template #after>
              <span v-if="groupCountry" class="country-value">{{
                groupCountry.name
              }}</span>
            </template>
            <template #footer>
              <span v-if="groupCountry"
                >{{ groupCountry.dialCode }} · {{ groupCountry.countryCode2 }} ·
                {{ groupCountry.currencyCode }}</span
              >
            </template>
          </f7-list-item>
          <f7-list-input
            :label="$t('common.locality')"
            type="text"
            :placeholder="$t('group.cityPlaceholder')"
            :value="form.city ||groupCountry.capitalCity"
            @input="form.city = $event.target.value"
          />
        </f7-list>

        <!-- STEP 3 · START -->
        <f7-block-title large v-if="step === 3">{{
          $t("group.stepStart")
        }}</f7-block-title>
        <f7-block v-if="step === 3">
          <GroupStarterModePicker v-model="form.starterMode" />
        </f7-block>

        <template v-if="step === 3 && marketplaceRecommended">
          <f7-block-title medium>{{
            $t("marketplace.settingsTitle")
          }}</f7-block-title>
          <f7-block>
            <div
              class="feature-chip marketplace-toggle"
              :class="{ selected: marketplaceEnabled }"
              @click="toggleMarketplace"
            >
              <f7-icon f7="cart_fill" size="16" />
              <div class="feature-chip-text">
                <span class="feature-chip-name">{{
                  $t("marketplace.featureMarketplace")
                }}</span>
                <span class="feature-chip-desc">{{
                  $t("marketplace.featureMarketplaceDesc")
                }}</span>
              </div>
              <f7-icon
                class="feature-chip-check"
                f7="checkmark_circle_fill"
                size="18"
              />
            </div>
            <p class="feature-hint">
              {{ $t("marketplace.featureMarketplaceHint") }}
            </p>
            <MarketplaceSettings
              v-if="marketplaceEnabled"
              v-model="form.marketplace"
            />
          </f7-block>
        </template>

        <f7-block v-if="step === 3 && !marketplaceRecommended">
          <div class="recommend-card">
            <f7-icon f7="bag_fill" size="18" />
            <p class="recommend-text">
              {{ $t("group.marketplaceRecommendText") }}
            </p>
          </div>
        </f7-block>

        <!-- STEP 3 CONFIRM -->
        <f7-block
          strong
          inset
          v-if="step === 3 && canContinueForConfirm"
          class="review-block"
        >
          <f7-block-title>{{
            $t("group.confirmYouAreCreating")
          }}</f7-block-title>
          <div class="review-row">
            <span>{{ $t("common.name") }}</span
            ><strong>{{ form.name || "—" }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("group.Category") }}</span
            ><strong>{{ $t(categoryAndTypesSummary) }}</strong>
          </div>
          <div class="review-row">
            <span>{{ $t("group.stepStart") }}</span
            ><strong>{{
              form.starterMode ? t(starterLabel(form.starterMode)) : "—"
            }}</strong>
          </div>
          <div v-if="marketplaceEnabled" class="review-row">
            <span>{{ $t("group.featuresStep") }}</span
            ><strong>{{ $t("marketplace.featureMarketplace") }}</strong>
          </div>
        </f7-block>

        <!-- STEP 4 · REVIEW & CREATE -->
        <f7-block-title large v-if="step === 4">{{
          $t("group.stepReview")
        }}</f7-block-title>
        <div v-if="step === 4">
          <div class="review-intro-card margin-horizontal">
            <p class="review-intro">{{ $t("group.reviewIntro") }}</p>
            <p class="review-intro-sub">{{ $t("group.reviewIntroSub") }}</p>
          </div>

          <f7-block strong inset class="review-block">
            <f7-block-title>{{
              $t("group.confirmYouAreCreating")
            }}</f7-block-title>
            <div class="review-row">
              <span>{{ $t("common.name") }}</span
              ><strong>{{ form.name || "—" }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("group.Category") }}</span
              ><strong>{{ $t(categoryAndTypesSummary) }}</strong>
            </div>
            <div class="review-row">
              <span>{{ $t("group.stepStart") }}</span
              ><strong>{{
                form.starterMode ? t(starterLabel(form.starterMode)) : "—"
              }}</strong>
            </div>
            <div v-if="marketplaceEnabled" class="review-row">
              <span>{{ $t("group.featuresStep") }}</span
              ><strong>{{ $t("marketplace.featureMarketplace") }}</strong>
            </div>
          </f7-block>

          <f7-block inset class="finance-hint-block">
            <f7-icon f7="info_circle" size="16" class="hint-icon" />
            <div class="hint-text">
              <p class="hint-title">{{ $t("group.financeLaterTitle") }}</p>
              <p class="hint-desc">{{ $t("group.financeLaterDesc") }}</p>
            </div>
          </f7-block>
        </div>

        <f7-block strong inset class="after-create-hint" v-if="step === 4">
          <p>{{ $t("group.afterCreateHint") }}</p>
          <ul>
            <li>{{ $t("group.afterCreateInvite") }}</li>
            <li>{{ $t("group.afterCreateCampaign") }}</li>
            <li>{{ $t("group.afterCreateFinance") }}</li>
          </ul>
        </f7-block>

        <f7-toolbar bottom class="create-footer no-hairlines">
          <div :class="['create-footer-inner', { 'with-back': step > 1 }]">
            <f7-button
              v-if="step > 1"
              class="create-footer-back"
              outline
              round
              large
              @click="step--"
            >
              {{ $t("common.back") }}
            </f7-button>
            <f7-button
              class="create-footer-primary"
              large
              fill
              round
              :disabled="!canContinue || submitting"
              :loading="submitting"
              @click="continueOrSubmit"
            >
              {{
                step < totalSteps
                  ? $t("group.creationNextStep")
                  : $t("group.creationValidate")
              }}
            </f7-button>
          </div>
        </f7-toolbar>
      </template>

      <template #right>
        <GroupCreationSummary
          v-if="form.category"
          variant="rail"
          :category-code="form.category"
          :types="derivedTypes"
          :step="step"
        />
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from "vue";
import _ from "lodash";
import { f7 } from "framework7-vue";
import AppLayout from "@/components/common/AppLayout.vue";
import GroupCategoryPicker from "../components/group/GroupCategoryPicker.vue";
import GroupCreationSummary from "../components/tamtam/GroupCreationSummary.vue";
import GroupPrimaryTypePicker from "../components/group/GroupPrimaryTypePicker.vue";
import GroupSecondaryTypePicker from "../components/group/GroupSecondaryTypePicker.vue";
import GroupStarterModePicker from "../components/group/GroupStarterModePicker.vue";
import MarketplaceSettings from "../components/marketplace/MarketplaceSettings.vue";
import { MARKETPLACE_DEFAULTS } from "../js/pinia-stores/marketplace.js";
import {
  groupCategoryLabel,
  groupTypeLabel,
  GROUP_TAG_GROUPS,
  GROUP_CATEGORIES,
} from "../constants/group-taxonomy.js";
import { useLocationStore } from "../js/pinia-stores/location.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useHaptics } from "../js/composables/useHaptics.js";
import i18n from "../js/i18n.js";
const { t } = i18n.global;
const haptic = useHaptics();

const GROUP_DRAFT_KEY = "avocash:create-group:draft:v3";
const BUSINESS_TYPES = [
  "COLLEAGUES",
  "TRADERS_INFORMAL",
  "PROFESSIONALS_FORMAL",
  "FARMERS_COOPERATIVE",
  "ENTREPRENEURS",
  "UNION_PROFESSIONAL_ORDER",
  "ADVERTISERS_BRANDS",
];
const step = ref(1);
const submitting = ref(false);
const draftSaved = ref(false);
const selectedCountry = ref(null);
const locationStore = useLocationStore();
const groupsStore = useGroupsStore();
const keycloak = useKeycloakStore();

const form = reactive({
  category: null,
  primaryType: null,
  secondaryTypes: [],
  name: "",
  description: "",
  city: "",
  visibility: "PRIVATE",
  features: [],
  marketplace: JSON.parse(JSON.stringify(MARKETPLACE_DEFAULTS)),
  // Financial features are now enabled progressively after group creation
  // No longer collected during initial creation
  initialFinancialFeatures: [],
});

if (keycloak.authenticated) {
  selectedCountry.value = keycloak?.profile?.countryCode || null;
}

const country = computed(() => locationStore.country);

const countries = computed(() => {
  return Array.isArray(country.value)
    ? _.groupBy(country.value, "continentName")
    : {};
});

const groupCountry = computed(() => {
  if (!selectedCountry.value) return null;
  const countryList = locationStore.country || [];
  return countryList.find((c) => c.countryCode2 === selectedCountry.value);
});

// Ensure selectedCountry is set from keycloak profile when location store loads
watch(
  () => locationStore.country,
  (countries) => {
    if (
      countries?.length &&
      !selectedCountry.value &&
      keycloak.profile?.countryCode
    ) {
      const match = countries.find(
        (c) => c.countryCode2 === keycloak.profile.countryCode,
      );
      if (match) {
        selectedCountry.value = match.countryCode2;
      }
    }
  },
  { immediate: true },
);
const derivedTypes = computed(() =>
  [form.primaryType, ...form.secondaryTypes].filter(Boolean),
);
const categoryAndTypesSummary = computed(() => {
  const cat = groupCategoryLabel(form.category);
  const typeLabels = derivedTypes.value.map(groupTypeLabel);
  const translatedTypes = typeLabels.map((key) => t(key)).join(" + ");
  return translatedTypes ? `${t(cat)} · ${translatedTypes}` : t(cat);
});
const isBusinessType = computed(() =>
  derivedTypes.value.some((c) => BUSINESS_TYPES.includes(c)),
);
const marketplaceEnabled = computed(() =>
  form.features.includes("MARKETPLACE"),
);
const marketplaceRecommended = computed(
  () => isBusinessType.value || form.starterMode === "COMMERCE",
);

// Derived tags based on taxonomy and selected criteria
const derivedTags = computed(() => {
  const tags = new Set();

  // Language tag (from GROUP_TAG_GROUPS taxonomy - matches GroupTag enum)
  try {
    const locale = i18n.global.locale.value;
    if (locale === "fr") tags.add("LANGUAGE_FR");
    else if (locale === "en") tags.add("LANGUAGE_EN");
    else if (locale.startsWith("es")) tags.add("LANGUAGE_ES");
    else if (locale.startsWith("de")) tags.add("LANGUAGE_DE");
  } catch {}

  // Country tag (from selected country - must match GroupTag enum COUNTRY_XX)
  if (selectedCountry.value) {
    const countryTag = `COUNTRY_${selectedCountry.value}`;
    // Only add if it's a known country in the enum
    const validCountryTags = [
      "COUNTRY_CI",
      "COUNTRY_SN",
      "COUNTRY_ML",
      "COUNTRY_BF",
      "COUNTRY_TG",
      "COUNTRY_BJ",
      "COUNTRY_GN",
      "COUNTRY_NE",
      "COUNTRY_FR",
    ];
    if (validCountryTags.includes(countryTag)) {
      tags.add(countryTag);
    }
  }

  // Filter to only tags that exist in GROUP_TAG_GROUPS taxonomy (which mirrors GroupTag enum)
  const validTagCodes = new Set();
  GROUP_TAG_GROUPS.forEach((group) => {
    group.tags.forEach((tag) => validTagCodes.add(tag.code));
  });

  return Array.from(tags).filter((tag) => validTagCodes.has(tag));
});

const steps = computed(() => {
  const list = [
    { key: "PURPOSE", title: t("group.stepPurpose") },
    { key: "IDENTITY", title: t("group.stepIdentity") },
    { key: "START", title: t("group.stepStart") },
    { key: "REVIEW", title: t("group.stepReview") },
  ];
  return list;
});
const totalSteps = computed(() => steps.value.length);
const currentStep = computed(() => steps.value[step.value - 1]);

const ownerDefaultName = computed(() => {
  const profile = keycloak.profile || {};
  const fromProfile =
    profile?.name ||
    [profile?.givenName, profile?.familyName].filter(Boolean).join(" ") ||
    keycloak.tokenParsed?.name ||
    keycloak.tokenParsed?.preferred_username;
  if (fromProfile) return String(fromProfile);
  return form.name || "Group owner";
});

const canContinue = computed(() => {
  const key = currentStep.value?.key;
  if (key === "PURPOSE") return !!form.category && !!form.primaryType;
  if (key === "IDENTITY")
    return form.name.trim().length >= 2 && !!selectedCountry.value;
  if (key === "START") return !!form.starterMode;
  if (key === "REVIEW") return !!form.name?.trim();
  return false;
});
const canContinueForConfirm = computed(() => !!form.name?.trim());

const nextStepHint = computed(() => {
  const key = currentStep.value?.key;
  if (key === "PURPOSE") return t("group.purposeHint");
  if (key === "IDENTITY") {
    if (!selectedCountry.value) return t("group.countryRequiredHint");
    return t("group.identityHint");
  }
  if (key === "START") return t("group.startHint");
  if (key === "REVIEW") return t("group.reviewHint");
  return "";
});

function starterLabel(id) {
  const map = {
    COMMUNITY: "groupStarter.community",
    MONEY: "groupStarter.money",
    COMMERCE: "groupStarter.commerce",
  };
  return map[id] || id;
}

function onCategoryChange(code) {
  form.category = code;
  form.primaryType = null;
  form.secondaryTypes = [];
}

function toggleMarketplace() {
  haptic.light();
  form.features = form.features.includes("MARKETPLACE")
    ? form.features.filter((f) => f !== "MARKETPLACE")
    : [...form.features, "MARKETPLACE"];
}

function continueOrSubmit() {
  if (submitting.value) return;
  if (step.value < totalSteps.value) {
    haptic.light();
    step.value += 1;
  } else {
    submit();
  }
}

function persistDraft() {
  try {
    localStorage.setItem(
      GROUP_DRAFT_KEY,
      JSON.stringify({
        step: step.value,
        savedAt: Date.now(),
        form: { ...form, marketplace: { ...form.marketplace } },
      }),
    );
    draftSaved.value = true;
  } catch (e) {
    draftSaved.value = false;
  }
}
const saveDraft = _.debounce(persistDraft, 600);

function restoreDraft() {
  try {
    const raw = localStorage.getItem(GROUP_DRAFT_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed?.form) return;
    Object.assign(form, parsed.form);
    if (parsed.step) {
      step.value = Math.min(Math.max(1, Number(parsed.step)), totalSteps.value);
    }
  } catch (e) {
    // ignore corrupted drafts
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(GROUP_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
  draftSaved.value = false;
}

watch(() => JSON.stringify({ step: step.value, form }), saveDraft);
watch(
  () => form.primaryType,
  (val) => {
    if (!val) return;
    form.secondaryTypes = form.secondaryTypes.filter((c) => c !== val);
  },
);
watch(totalSteps, (n) => {
  if (step.value > n) step.value = n;
});

async function submit() {
  if (submitting.value) return;
  submitting.value = true;
  f7.preloader.show();
  try {
    const payload = {
      createdBy: keycloak.subject,
      category: form.category,
      primaryType: form.primaryType,
      // secondaryTypes: form.secondaryTypes,
      types: derivedTypes.value,
      name: form.name.trim(),
      description: form.description.trim() || null,
      city: form.city.trim() || groupCountry.value?.capitalCity || null,
      tags: derivedTags.value,
      countryCode:
        selectedCountry.value || keycloak.profile?.countryCode || null,
      legalCountry:
        selectedCountry.value || keycloak.profile?.countryCode || null,
      primaryOperatingCountry:
        selectedCountry.value || keycloak.profile?.countryCode || null,
      defaultCurrency:
        groupCountry.value?.currencyCode ||
        keycloak.profile?.currencyCode ||
        null,
      visibility: form.visibility,
      features: form.features,
      marketplace: marketplaceEnabled.value ? form.marketplace : null,
      // Financial features are now enabled progressively after group creation
      // No longer collected during initial creation
      initialFinancialFeatures: form.initialFinancialFeatures,
    };
    console.log("Creating group with payload:", payload);
    const group = await groupsStore.createGroup(payload);
    submitting.value = false;
    f7.preloader.hide();
    clearDraft();
    showSuccess(group);
  } catch (e) {
    submitting.value = false;
    f7.preloader.hide();
    console.error("Group creation error:", e);
    console.error("Error response:", e?.response?.data);
    console.error("Error status:", e?.response?.status);
    haptic.error();
    f7.dialog.alert(
      e?.response?.data?.message ||
        e?.message ||
        t("group.creationErrorMessage"),
    );
  }
}

function showSuccess(group) {
  const router = f7.views?.main?.router;
  f7.dialog
    .create({
      title: t("groupSuccess.title"),
      text: t("groupSuccess.message"),
      buttons: [
        {
          text: t("groupSuccess.invite"),
          onClick: () =>
            router?.navigate(`/groups/${group.id}/members/import`, {
              props: { group },
            }),
        },
        {
          text: t("groupSuccess.view"),
          bold: true,
          onClick: () =>
            router?.navigate(`/groups/${group.id}`, { props: { group } }),
        },
        {
          text: t("groupSuccess.enableFinance"),
          onClick: () =>
            router?.navigate(`/groups/${group.id}/financial-setup`, {
              props: { group },
            }),
        },
      ],
      verticalButtons: true,
    })
    .open();
}

onMounted(() => {
  if (keycloak.authenticated && keycloak?.profile?.countryCode) {
    selectedCountry.value = keycloak?.profile?.countryCode;
  }
  restoreDraft();
});
</script>

<style scoped>
.draft-saved {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 8px 16px 0;
}
.section-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
  margin: 18px 0 8px;
}
.section-muted {
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 4px 2px 16px;
}
.field-hint {
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 0 18px;
  margin: -4px 0 0;
}
.selected-category-recap {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 12px 8px 4px;
}
@media (min-width: 1200px) {
  .create-summary-mobile {
    display: none;
  }
}

/* Express header banner — replaces Tailwind-style utilities with semantic CSS.
   Gradient: primary 10% → surface-container → secondary-container 20%, 16px
   radius, hairline border, 16px padding (spacing grid 4/8/12/16/24/32). */
.express-banner {
  margin: 8px 0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid color-mix(in srgb, #000 5%, transparent);
}
.express-banner-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--f7-theme-color) 10%, transparent),
    var(--f7-surface-container, var(--f7-list-bg-color, #f4f4f5)) 45%,
    color-mix(
      in srgb,
      var(--f7-secondary-container, #e8def8) 20%,
      transparent
    )
  );
}
.express-banner-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--f7-theme-color);
  color: var(--f7-on-primary, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.express-banner-text {
  flex: 1;
  min-width: 0;
}
.express-banner-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.express-banner-badge {
  font-size: var(--f7-label-sm-font-size, 11px);
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.express-banner-time {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: var(--f7-label-sm-font-size, 11px);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.express-banner-time .f7-icon {
  color: var(--f7-theme-color);
}
.express-banner-title {
  margin: 0;
  font-size: var(--f7-headline-sm-font-size, 16px);
  font-weight: 700;
  line-height: 1.35;
  color: var(--f7-text-color, #1c1c1e);
}
.express-banner-desc {
  margin: 0;
  font-size: var(--f7-body-sm-font-size, 13px);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.step-label {
  font-size: 12px;
  color: var(--f7-theme-color);
  font-weight: 500;
}
.feature-chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.feature-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px; /* M3 minimum touch target (#133) */
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  background: var(--f7-page-bg-color, #fff);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.1s;
}
.feature-chip:active {
  transform: scale(0.98); /* press feedback within 100ms (#135) */
}
.feature-chip.selected {
  border: 1.5px solid var(--f7-theme-color);
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
}
.feature-chip-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.feature-chip-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.feature-chip.selected .feature-chip-name {
  color: var(--f7-theme-color);
}
.feature-chip-desc {
  font-size: 12px;
  line-height: 1.4;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.feature-chip-check {
  flex-shrink: 0;
  color: var(--f7-theme-color);
}
.feature-hint {
  font-size: 12px;
  line-height: 1.55;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 8px 2px 0;
}
.marketplace-toggle {
  margin-bottom: 8px;
}
.recommend-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--f7-list-bg-color, #f5f5f5);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.recommend-card .f7-icon {
  color: var(--f7-theme-color);
  flex-shrink: 0;
  margin-top: 2px;
}
.recommend-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}
.funds-intro-card {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
}
.funds-intro {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.funds-intro-sub {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.review-title {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.review-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}
.review-row:last-child {
  border-bottom: none;
}
.review-row strong {
  text-align: right;
}

/* Stretch the main column so the footer sits at the bottom of the viewport. */
:deep(.main_layout) {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - var(--f7-navbar-height, 56px));
  min-height: calc(100dvh - var(--f7-navbar-height, 56px));
}

/* Footer buttons — flex replaces the `grid cols25_75` utility combo:
   with-back → 25% back button + flexible primary, else full-width primary. */
.create-footer-inner {
  display: flex;
  width: 100%;
  gap: 8px;
  padding: 0 16px;
}
.create-footer-inner .create-footer-primary {
  flex: 1;
}
.create-footer-inner.with-back .create-footer-back {
  flex: 0 0 25%;
}

/* Bottom toolbar styling */
.create-footer {
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 20;
  /* border-top: 1px solid var(--f7-list-item-border-color, #e0e0e0); */
  padding: 8px 16px calc(8px + var(--f7-safe-area-bottom, 0px));
  background: transparent;
}
@media (min-width: 1200px) {
  .create-footer {
    position: static;
  }
}

.next-steps-hint {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
  margin-bottom: 8px;
}

/* Review & hint blocks */
.review-intro-card {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
}
.review-intro {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.review-intro-sub {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}

.finance-hint-block {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 20%, transparent);
  margin-top: 16px;
}
.hint-icon {
  color: var(--f7-theme-color);
  flex-shrink: 0;
  margin-top: 2px;
}
.hint-text {
  flex: 1;
  min-width: 0;
}
.hint-title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.hint-desc {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}

.next-steps-hint {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 12px;
}
.next-steps-hint p {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
}
.next-steps-hint ul {
  margin: 0;
  padding-left: 20px;
}
.next-steps-hint li {
  margin: 4px 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
}
</style>
